import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { User } from '../api/types';
import { IconDownload, IconEye, IconEyeOff, IconMail } from '../components/Icons';
import { LogoBadge } from '../components/Logo';
import { useTheme } from '../components/Toast';
import { useT } from '../i18n';

export function Login({
  onSignedIn,
  onRegister,
}: {
  onSignedIn: (user: User) => void;
  onRegister: () => void;
}) {
  const { t, lang, setLang } = useT();
  // Called for the side effect: the login screen should honour the saved theme.
  useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  /**
   * The current Android build, read from the same manifest the app itself
   * checks on launch — so the download here can never offer a different
   * version from the one an installed app is being told to update to.
   */
  const [appVersion, setAppVersion] = useState<{ latest: string; url: string } | null>(null);

  useEffect(() => {
    fetch('/app/version.json')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.latest && data?.url) setAppVersion({ latest: data.latest, url: data.url });
      })
      // No manifest published yet, or no network. The link simply does not
      // appear; a broken download offered on the sign-in page is worse than
      // none.
      .catch(() => {});
  }, []);

  // The support address comes from the server so it is set once, in .env,
  // rather than baked into the bundle at build time.
  useEffect(() => {
    api
      .health()
      .then((info) => setSupportEmail(info.supportEmail ?? ''))
      .catch(() => setSupportEmail(''));
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api.login(email, password));
    } catch (cause) {
      // 401 gets the translated sentence; anything else (rate limited, server
      // down) shows what the server said, because "something went wrong" tells
      // the person nothing about whether to wait, retry, or call for help.
      if (cause instanceof ApiError) {
        setError(cause.status === 401 ? t('invalidLogin') : cause.message);
      } else {
        setError(t('serverUnreachable'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <LogoBadge size={52} />
          <h1>Yamas</h1>
          <p className="tagline">{t('tagline')}</p>
        </div>

        <label>
          {t('email')}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            dir="ltr"
          />
        </label>

        <label>
          {t('password')}
          <span className="input-with-action">
            <input
              type={reveal ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              dir="ltr"
            />
            <button
              type="button"
              className="reveal-btn"
              onClick={() => setReveal((value) => !value)}
              aria-label={reveal ? t('hidePassword') : t('showPassword')}
              title={reveal ? t('hidePassword') : t('showPassword')}
            >
              {reveal ? <IconEyeOff /> : <IconEye />}
            </button>
          </span>
        </label>

        {error && <p className="form-error">{error}</p>}

        <button type="submit" className="btn primary block" disabled={busy}>
          {busy && <span className="btn-spinner" />}
          {busy ? t('loading') : t('signIn')}
        </button>

        <button type="button" className="btn block" onClick={onRegister}>
          {t('noAccount')}
        </button>

        {appVersion && (
          <a className="btn block" href={appVersion.url} download>
            <IconDownload />
            {t('downloadApp')} {appVersion.latest}
          </a>
        )}

        <div className="login-footer">
          <button type="button" className="link-btn" onClick={() => setForgot(true)}>
            {t('forgotPassword')}
          </button>
          <button type="button" className="link-btn" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}>
            {lang === 'ar' ? 'English' : 'العربية'}
          </button>
        </div>
      </form>

      {forgot && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-icon" style={{ background: 'var(--brand-soft)', color: 'var(--brand-500)' }}>
              <IconMail size={22} />
            </div>
            <h3>{t('forgotPassword')}</h3>
            <p className="muted small" style={{ margin: 0 }}>
              {t('forgotBody')}
            </p>

            {supportEmail ? (
              <a className="support-address" href={`mailto:${supportEmail}?subject=Password%20reset`}>
                {supportEmail}
              </a>
            ) : (
              <p className="muted small" style={{ margin: 0 }}>
                {t('forgotNoAddress')}
              </p>
            )}

            <button type="button" className="btn block" onClick={() => setForgot(false)}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
